import React, { useState } from "react";
import { Link } from "react-router-dom";
import SocialMediaLinks from "./SocialMediaLinks";
import CommentList from "./CommentList";

const Footer: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [email, setEmail] = useState("");
  const [previewCourse, setPreviewCourse] = useState<string | null>(null);

  const coursePreviewData: Record<string, { title: string, description: string, image: string }> = {
    "SCIENCE 11": {
      title: "Science 11",
      description: "Comprehensive foundation in Physics, Chemistry, and Biology for Class 11 students.",
      image: "/images/science11.jpg"
    },
    "SCIENCE 12": {
      title: "Science 12",
      description: "Advanced concepts in scientific disciplines to prepare for board exams and competitive tests.",
      image: "/images/science12.jpg"
    },
    "ARTS 11": {
      title: "Arts 11",
      description: "Humanities and social sciences curriculum designed for Class 11 students.",
      image: "/images/arts11.jpg"
    },
    "ARTS 12": {
      title: "Arts 12",
      description: "Advanced studies in humanities subjects for Class 12 board preparation.",
      image: "/images/arts12.jpg"
    },
    "CLASS 10": {
      title: "Class 10",
      description: "Foundation courses covering all subjects for Class 10 board exams.",
      image: "/images/class10.jpg"
    },
    "NEET 11": {
      title: "NEET 11",
      description: "Specialized coaching for NEET aspirants in Class 11.",
      image: "/images/neet11.jpg"
    },
    "NEET 12": {
      title: "NEET 12",
      description: "Intensive NEET preparation program for Class 12 students.",
      image: "/images/neet12.jpg"
    }
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubscribed(true);
    setEmail("");
    // In a real application, you would send this to your backend
  };

  const handleMouseEnter = (course: string) => {
    setPreviewCourse(course);
  };

  const handleMouseLeave = () => {
    setPreviewCourse(null);
  };

  return (
    <footer className="bg-gray-800 text-gray-300 py-8 px-4 relative">
      <CommentList />
      <div className="container mx-auto text-center md:text-left space-y-6">
        {/* Logo and Title with hover effect */}
        <div 
          className="flex flex-col items-center md:items-start mb-6 transition-transform duration-300 hover:scale-105"
          onMouseEnter={() => setActiveSection("logo")}
          onMouseLeave={() => setActiveSection(null)}
        >
          <img 
            src="/logo192.png" 
            alt="Logo" 
            className={`w-24 h-24 mb-2 rounded-full ${activeSection === "logo" ? "animate-pulse" : ""}`} 
          />
          <span className="font-bold flex flex-col text-white text-center md:text-left">
            <span className="text-4xl font-extrabold mt-3">N A E</span>
            <span className="text-md">COACHING</span>
          </span>
        </div>

        <SocialMediaLinks />

        {/* Newsletter Subscription */}
        <div className="mb-6 p-4 bg-gray-700 rounded-lg transition-all duration-300 hover:bg-gray-600">
          <h3 className="text-white font-bold mb-2">Subscribe to Our Newsletter</h3>
          {!isSubscribed ? (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2">
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address" 
                className="px-4 py-2 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button 
                type="submit" 
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors duration-300"
              >
                Subscribe
              </button>
            </form>
          ) : (
            <div className="text-green-300 py-2 px-4 rounded-md bg-gray-800 animate-fade-in">
              Thank you for subscribing! Check your email for confirmation.
            </div>
          )}
        </div>

        {/* Course Preview Section */}
        <div className="mb-6">
          <h3 className="text-white font-bold mb-2">Our Courses</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm md:text-base mb-6 relative">
            {Object.keys(coursePreviewData).map((course) => (
              <Link 
                key={course}
                to={`/CoursesPage/${course}`} 
                className="hover:text-blue-300 transition-colors duration-300"
                onMouseEnter={() => handleMouseEnter(course)}
                onMouseLeave={handleMouseLeave}
              >
                {course.replace(/([0-9]+)/, " $1")}
              </Link>
            ))}
            
            {/* Live Preview Popup */}
            {previewCourse && coursePreviewData[previewCourse] && (
              <div className="absolute left-0 right-0 -top-32 bg-gray-900 p-4 rounded-lg shadow-lg z-10 transition-opacity duration-300 animate-fade-in">
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                  <div className="w-24 h-24 bg-gray-700 rounded-md overflow-hidden">
                    <img 
                      src={coursePreviewData[previewCourse].image} 
                      alt={coursePreviewData[previewCourse].title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/api/placeholder/100/100";
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-bold">{coursePreviewData[previewCourse].title}</h4>
                    <p className="text-gray-300 text-sm">{coursePreviewData[previewCourse].description}</p>
                    <span className="inline-block mt-2 text-blue-300 text-xs">Click to view course details →</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Links - Interactive Tabs */}
        <div className="mb-6">
          <h3 className="text-white font-bold mb-2">Quick Links</h3>
          <div className="flex flex-wrap gap-2">
            {[
              { path: "/", label: "Home" },
              { path: "/courses", label: "Courses" },
              { path: "/progress", label: "Progress" },
              { path: "/profile", label: "Profile" },
              { path: "/more", label: "More" },
              { path: "/about-us", label: "About Us" }
            ].map((link) => (
              <Link 
                key={link.path}
                to={link.path} 
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors duration-300"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Location and Contact Information with animation */}
        <div 
          className="text-sm md:text-base mb-4 space-y-1 text-center md:text-left p-4 bg-gray-700 rounded-lg hover:shadow-lg transition-all duration-300"
          onMouseEnter={() => setActiveSection("contact")}
          onMouseLeave={() => setActiveSection(null)}
        >
          <h3 className="text-white font-bold mb-2">Find Us</h3>
          <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
            <div className={`transition-all duration-500 ${activeSection === "contact" ? "scale-105" : ""}`}>
              <p className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Near Railway Gate, Thakurganj, Bihar
              </p>
              <p className="flex items-center gap-2 mt-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                Contact: +91 7979061569
              </p>
            </div>
            <button 
              className="mt-2 md:mt-0 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-md text-white transition-colors duration-300"
              onClick={() => window.open("https://maps.google.com/?q=Thakurganj,Bihar", "_blank")}
            >
              View on Map
            </button>
          </div>
        </div>

        {/* Tagline with animation */}
        <p className="text-lg italic text-gray-400 mb-4 text-center md:text-left hover:text-white transition-colors duration-300">
          We don't promise, We've Plans.
        </p>

        {/* Footer Bottom Text */}
        <div className="flex flex-col md:flex-row justify-between items-center text-xs text-gray-500 pt-4 border-t border-gray-700">
          <p>© {new Date().getFullYear()} NAE Coaching. All rights reserved.</p>
          <div className="flex gap-4 mt-2 md:mt-0">
            <Link to="/privacy-policy" className="hover:text-gray-300 transition-colors duration-300">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gray-300 transition-colors duration-300">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;